export const EXTERNAL_PAYMENT_METHODS = {
  bank_transfer: 'Bank transfer',
  direct_debit: 'Direct Debit',
  payment_link: 'Payment link',
  cash: 'Cash',
  cheque: 'Cheque',
  other: 'Other arrangement',
};

const FIELDS = {
  accountName: 'Account name',
  bankDetails: 'Bank details',
  paymentLink: 'Payment link',
  directDebitLink: 'Direct Debit setup link',
  reference: 'Payment reference',
  timing: 'When to pay',
  contact: 'Payment help',
  notes: 'Additional instructions',
};

function selectedMethods(details) {
  const selected = Array.isArray(details.methods) ? details.methods : [details.method];
  return Object.keys(EXTERNAL_PAYMENT_METHODS).filter(key => selected.includes(key));
}

export function buildExternalPaymentInstructions(details) {
  const methods = selectedMethods(details);
  if (!methods.length) return '';
  if (methods.length === 1 && methods[0] === 'other' && details.notes?.trim()
    && !Object.keys(FIELDS).some(key => key !== 'notes' && String(details[key] || '').trim())) {
    return details.notes.trim();
  }
  const rows = [`Payment methods: ${methods.map(key => EXTERNAL_PAYMENT_METHODS[key]).join(', ')}`];
  for (const [key, label] of Object.entries(FIELDS)) {
    if ((key === 'accountName' || key === 'bankDetails') && !methods.includes('bank_transfer')) continue;
    if (key === 'paymentLink' && !methods.includes('payment_link')) continue;
    if (key === 'directDebitLink' && !methods.includes('direct_debit')) continue;
    const value = String(details[key] || '').trim();
    if (value) rows.push(`${label}: ${value}`);
  }
  return rows.join('\n');
}

export function parseExternalPaymentInstructions(instructions = '', draft) {
  const empty = { methods: [], ...Object.fromEntries(Object.keys(FIELDS).map(key => [key, ''])) };
  if (draft && typeof draft === 'object' && Array.isArray(draft.methods)) {
    return { ...empty, methods: selectedMethods(draft), ...Object.fromEntries(Object.keys(FIELDS).map(key => [key, typeof draft[key] === 'string' ? draft[key] : ''])) };
  }
  const fallback = { ...empty, methods: instructions.trim() ? ['other'] : [], notes: instructions };
  const text = instructions.trim();
  const firstLine = text.split('\n')[0];
  const legacy = firstLine.startsWith('Payment method: ');
  const prefix = legacy ? 'Payment method: ' : 'Payment methods: ';
  if (!firstLine.startsWith(prefix)) return fallback;
  const labels = firstLine.slice(prefix.length).split(', ');
  const methods = labels.map(label => Object.keys(EXTERNAL_PAYMENT_METHODS).find(key => EXTERNAL_PAYMENT_METHODS[key] === label));
  if (methods.some(key => !key) || new Set(methods).size !== methods.length) return fallback;
  const details = { ...empty, methods };
  let currentKey;
  for (const line of text.split('\n').slice(1)) {
    const key = Object.keys(FIELDS).find(field => line.startsWith(`${FIELDS[field]}: `));
    if (key) {
      if (details[key]) return fallback;
      currentKey = key;
      details[key] = line.slice(FIELDS[key].length + 2);
    } else if (currentKey) details[currentKey] += `\n${line}`;
    else return fallback;
  }
  let normalizedText = text.replace(/^Payment method: /, 'Payment methods: ');
  if (legacy && methods.length === 1 && methods[0] === 'direct_debit' && details.paymentLink) {
    details.directDebitLink = details.paymentLink;
    details.paymentLink = '';
    normalizedText = normalizedText.replace(/\nPayment link: /, '\nDirect Debit setup link: ');
  }
  // Preserve existing free-form instructions if they do not match this format.
  return buildExternalPaymentInstructions(details) === normalizedText ? details : fallback;
}

export function validateExternalPaymentDetails(details) {
  const methods = selectedMethods(details);
  if (!methods.length) return 'Select at least one way for members to pay.';
  if (methods.includes('bank_transfer') && (!details.accountName.trim() || !details.bankDetails.trim())) {
    return 'Add the club account name and bank details for bank transfer.';
  }
  if (methods.includes('payment_link') && !details.paymentLink.trim()) return 'Add the club payment link.';
  for (const [method, key] of [['payment_link', 'paymentLink'], ['direct_debit', 'directDebitLink']]) {
    if (!methods.includes(method) || !String(details[key] || '').trim()) continue;
    try {
      const url = new URL(details[key].trim());
      if (url.protocol !== 'https:') throw new Error();
    } catch {
      return 'Use a full secure payment link starting with https://.';
    }
  }
  if (methods.some(method => ['other', 'cash', 'cheque'].includes(method)
      || (method === 'direct_debit' && !String(details.directDebitLink || '').trim()))
    && !details.notes.trim() && !details.contact.trim()) {
    return 'Add payment instructions or a contact so members know how to pay.';
  }
  return '';
}
